import { useCallback, useEffect, useMemo, useState } from "react";

import { api, type Account, type AccountInput, ApiError } from "../lib/api";
import AccountForm from "../components/AccountForm";
import Modal from "../components/Modal";

type Mode =
  | { kind: "closed" }
  | { kind: "create" }
  | { kind: "edit"; account: Account };

export default function Accounts() {
  const [accounts, setAccounts] = useState<Account[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [mode, setMode] = useState<Mode>({ kind: "closed" });
  const [busyId, setBusyId] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const items = await api.list();
      setAccounts(items);
    } catch (e) {
      setError(messageFrom(e));
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const sorted = useMemo(
    () => (accounts ? [...accounts].sort((a, b) => a.name.localeCompare(b.name)) : null),
    [accounts],
  );

  async function handleCreate(input: AccountInput) {
    await api.create(input);
    setMode({ kind: "closed" });
    await refresh();
  }

  async function handleUpdate(id: string, patch: Partial<AccountInput>) {
    await api.update(id, patch);
    setMode({ kind: "closed" });
    await refresh();
  }

  async function handleDelete(id: string) {
    if (!confirm("Delete this account? This cannot be undone.")) return;
    setBusyId(id);
    try {
      await api.remove(id);
      await refresh();
    } catch (e) {
      setError(messageFrom(e));
    } finally {
      setBusyId(null);
    }
  }

  return (
    <section className="accounts">
      <header className="accounts-header">
        <h1>Accounts</h1>
        <button className="primary" onClick={() => setMode({ kind: "create" })}>
          + New account
        </button>
      </header>

      {error && <div className="error">{error}</div>}

      {sorted === null ? (
        <p>Loading&hellip;</p>
      ) : sorted.length === 0 ? (
        <p className="empty">No accounts yet. Click <strong>+ New account</strong> to add one.</p>
      ) : (
        <table className="accounts-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Prop firm</th>
              <th>Size</th>
              <th>Account #</th>
              <th>Strategies enabled</th>
              <th>Active</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((a) => (
              <tr key={a.account_id}>
                <td>{a.name}</td>
                <td>{a.prop_firm}</td>
                <td className="num">${a.size.toLocaleString()}</td>
                <td className="mono">{a.account_number}</td>
                <td>
                  {a.strategies_enabled.length === 0 ? (
                    <span className="muted">—</span>
                  ) : (
                    <ul className="tags">
                      {a.strategies_enabled.map((s) => (
                        <li key={s}>{s}</li>
                      ))}
                    </ul>
                  )}
                </td>
                <td>{a.active ? "Yes" : "No"}</td>
                <td className="row-actions">
                  <button
                    className="link-button"
                    onClick={() => setMode({ kind: "edit", account: a })}
                  >
                    Edit
                  </button>
                  <button
                    className="link-button danger"
                    disabled={busyId === a.account_id}
                    onClick={() => handleDelete(a.account_id)}
                  >
                    {busyId === a.account_id ? "Deleting…" : "Delete"}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <Modal open={mode.kind !== "closed"} onClose={() => setMode({ kind: "closed" })}>
        {mode.kind === "create" && (
          <AccountForm
            title="New account"
            onCancel={() => setMode({ kind: "closed" })}
            onSubmit={handleCreate}
          />
        )}
        {mode.kind === "edit" && (
          <AccountForm
            title={`Edit ${mode.account.name}`}
            initial={mode.account}
            onCancel={() => setMode({ kind: "closed" })}
            onSubmit={(patch) => handleUpdate(mode.account.account_id, patch)}
          />
        )}
      </Modal>
    </section>
  );
}

function messageFrom(e: unknown): string {
  if (e instanceof ApiError) return `${e.message} (HTTP ${e.status})`;
  if (e instanceof Error) return e.message;
  return String(e);
}
